import {
  AlertTriangleIcon,
  CalendarClockIcon,
  CirclePlayIcon,
  ClockIcon,
  PauseCircleIcon,
  RefreshCwIcon,
  XCircleIcon,
} from 'lucide-react';
import { FaRegCircleCheck } from 'react-icons/fa6';

import { JOB_STATUS } from '@/types/jobs';

export const JOBS_QUERY_KEY = 'jobs';
export const JOB_QUERY_KEY = 'job';
export const JOB_WORKSHEETS_QUERY_KEY = 'job-worksheets';
export const JOB_HISTORY_QUERY_KEY = 'job-history';

export const JOBS_PAGE_SIZE = 60;

export const JOB_STALE_TIME = 30000; // 30 seconds
export const JOB_GC_TIME = 5 * 60 * 1000; // 5 minutes

export const JOB_QUERY_OPTIONS = {
  staleTime: JOB_STALE_TIME,
  gcTime: JOB_GC_TIME,
};

/**
 * Job status labels mapping
 * Maps job status enum values to their display labels
 */
export const JOB_STATUS_LABELS: Record<JOB_STATUS, string> = {
  [JOB_STATUS.NEW]: 'NEW',
  [JOB_STATUS.SCHEDULED]: 'SCHEDULED',
  [JOB_STATUS.UNSCHEDULED]: 'UNSCHEDULED',
  [JOB_STATUS.SENT]: 'SENT',
  [JOB_STATUS.READ]: 'READ',
  [JOB_STATUS.ACCEPTED]: 'ACCEPTED',
  [JOB_STATUS.REFUSED]: 'REFUSED',
  [JOB_STATUS.ON_THE_WAY]: 'ON THE WAY',
  [JOB_STATUS.STARTED]: 'STARTED',
  [JOB_STATUS.SUSPENDED]: 'SUSPENDED',
  [JOB_STATUS.COMPLETED_OK]: 'COMPLETED',
  [JOB_STATUS.COMPLETED_WITH_ISSUES]: 'COMPLETED WITH ISSUES',
  [JOB_STATUS.CANCELLED]: 'CANCELLED',
  [JOB_STATUS.LATE_START]: 'LATE START',
  [JOB_STATUS.LATE_FINISH]: 'LATE FINISH',
  [JOB_STATUS.RESCHEDULED]: 'RESCHEDULED',
};

/**
 * Job status colors mapping
 * Maps job status enum values to their color classes
 */
export const JOB_STATUS_COLORS: Record<JOB_STATUS, string> = {
  [JOB_STATUS.NEW]: 'gray.500',
  [JOB_STATUS.SCHEDULED]: 'alternative.blue',
  [JOB_STATUS.UNSCHEDULED]: 'alternative.gold',
  [JOB_STATUS.SENT]: 'alternative.blue',
  [JOB_STATUS.READ]: 'alternative.blue',
  [JOB_STATUS.ACCEPTED]: 'alternative.green',
  [JOB_STATUS.REFUSED]: 'alternative.red',
  [JOB_STATUS.ON_THE_WAY]: 'alternative.purple',
  [JOB_STATUS.STARTED]: 'alternative.purple',
  [JOB_STATUS.SUSPENDED]: 'alternative.gold',
  [JOB_STATUS.COMPLETED_OK]: 'alternative.green',
  [JOB_STATUS.COMPLETED_WITH_ISSUES]: 'alternative.gold',
  [JOB_STATUS.CANCELLED]: 'alternative.red',
  [JOB_STATUS.LATE_START]: 'alternative.gold',
  [JOB_STATUS.LATE_FINISH]: 'alternative.gold',
  [JOB_STATUS.RESCHEDULED]: 'alternative.purple',
};

/**
 * Job status categories for grouping
 */
export const JOB_STATUS_CATEGORIES = {
  PENDING: [
    JOB_STATUS.NEW,
    JOB_STATUS.SCHEDULED,
    JOB_STATUS.UNSCHEDULED,
    JOB_STATUS.SENT,
    JOB_STATUS.READ,
    JOB_STATUS.ACCEPTED,
  ],
  IN_PROGRESS: [JOB_STATUS.ON_THE_WAY, JOB_STATUS.STARTED],
  COMPLETED: [JOB_STATUS.COMPLETED_OK],
  CANCELLED: [JOB_STATUS.REFUSED, JOB_STATUS.CANCELLED],
  ISSUES: [
    JOB_STATUS.LATE_START,
    JOB_STATUS.LATE_FINISH,
    JOB_STATUS.RESCHEDULED,
    JOB_STATUS.COMPLETED_WITH_ISSUES,
    JOB_STATUS.SUSPENDED,
  ],
} as const;

/**
 * Job status category colors with opacity
 */
export const JOB_STATUS_CATEGORY_COLORS = {
  PENDING: {
    bg: 'tags.blue.bg',
    color: 'tags.blue.font',
  },
  IN_PROGRESS: {
    bg: 'tags.purple.bg',
    color: 'tags.purple.font',
  },
  COMPLETED: {
    bg: 'tags.green.bg',
    color: 'tags.green.font',
  },
  CANCELLED: {
    bg: 'tags.red.bg',
    color: 'tags.red.font',
  },
  ISSUES: {
    bg: 'tags.gold.bg',
    color: 'tags.gold.font',
  },
} as const;

/**
 * Job status icons mapping
 */
export const JOB_STATUS_ICONS = {
  [JOB_STATUS.NEW]: ClockIcon,
  [JOB_STATUS.SCHEDULED]: CalendarClockIcon,
  [JOB_STATUS.UNSCHEDULED]: ClockIcon,
  [JOB_STATUS.SENT]: ClockIcon,
  [JOB_STATUS.READ]: ClockIcon,
  [JOB_STATUS.ACCEPTED]: ClockIcon,
  [JOB_STATUS.REFUSED]: XCircleIcon,
  [JOB_STATUS.ON_THE_WAY]: CirclePlayIcon,
  [JOB_STATUS.STARTED]: CirclePlayIcon,
  [JOB_STATUS.SUSPENDED]: PauseCircleIcon,
  [JOB_STATUS.COMPLETED_OK]: FaRegCircleCheck,
  [JOB_STATUS.COMPLETED_WITH_ISSUES]: FaRegCircleCheck,
  [JOB_STATUS.CANCELLED]: XCircleIcon,
  [JOB_STATUS.LATE_START]: AlertTriangleIcon,
  [JOB_STATUS.LATE_FINISH]: AlertTriangleIcon,
  [JOB_STATUS.RESCHEDULED]: RefreshCwIcon,
} as const;
